import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import { get, del } from "../../helpers/api_helper";
import { useNavigate } from "react-router-dom";
import { DELETE_USER, USER_LIST } from "../../helpers/url_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css"; // Make sure this is imported for styling
import usersColumns from "components/Columns/UsersColumns";
import EditUserModal from "./EditUserModal";

const Users = () => {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const role = Number(authUser?.user?.role);
      setIsAdmin(role === 1);
    } catch {
      setIsAdmin(false);
    }
  }, []);
  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "customers", link: "#" },
  ];

  const [users, setUsers] = useState([]);
  const [pageIndex, setPageIndex] = useState(1); // Starts at 0
  const [pageSize, setPageSize] = useState(10); // Default page size
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const navigate = useNavigate();

  async function getUsersList(page, size, sort = [], search = "") {
    const sortKey = sort[0]?.id;
    const sortby = sort[0]?.desc ? "desc" : "asc";
    return await get(USER_LIST, {
      params: {
        page,
        size,
        sortKey,
        sortby,
        search,
      },
    });
  }

  const fetchUsers = async (page = 1, size = 10, sortBy = [], searchText) => {
    try {
      const response = await getUsersList(page, size, sortBy, searchText); // Assuming your API takes page & size
      const { items, totalPages } = response.data;

      setUsers(items);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const handleSearchChange = (text) => {
    setPageIndex(1);
    setSearchText(text);
  };

  useEffect(() => {
    fetchUsers(pageIndex, pageSize, sortBy, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, sortBy, searchText]);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const [editModalOpen, setEditModalOpen] = useState(false);

  const handleDelete = (customer) => {
    setSelectedUser(customer); // Set the customer to be deleted
    setModalOpen(true); // Open the modal
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen); // Close the modal
  };

  const confirmDelete = async () => {
    try {
      await del(DELETE_USER(selectedUser.ul_id)); // Delete the user
      toastr.success("User deleted successfully!");
      fetchUsers(pageIndex, pageSize, sortBy, searchText);
    } catch (error) {
      console.error(
        "Failed to delete user:",
        error.response?.data || error.message
      );
      toastr.error("Something went wrong while deleting.");
    }
    setModalOpen(false); // Close the modal after the operation
  };

  const columns = useMemo(
    () =>
      usersColumns(isAdmin ? handleEdit : null, isAdmin ? handleDelete : null),
    [isAdmin]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="CUstomer" breadcrumbItems={breadcrumbItems} />
        </Container>

        <Card>
          <CardBody>
            {users.length > 0 && (
              <TableContainer
                columns={columns || []}
                data={users || []}
                isPagination={true}
                isSortable={true}
                isGlobalFilter={true}
                customPageSize={pageSize}
                isAddOptions={isAdmin}
                addOptionTitle={"Add New User"}
                iscustomPageSize={false}
                isBordered={false}
                controlledPagination={{
                  pageIndex,
                  setPageIndex,
                  pageSize,
                  setPageSize,
                  totalPages,
                }}
                onSortChange={setSortBy}
                searchText={searchText}
                onSearchChange={handleSearchChange}
                handleAddClick={
                  isAdmin ? () => navigate("/add-user") : undefined
                }
              />
            )}
          </CardBody>
        </Card>
      </div>
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this user permanently?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </ModalFooter>
      </Modal>
      <EditUserModal
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(false)}
        customerData={selectedUser}
        onUpdated={() => fetchUsers(pageIndex, pageSize, sortBy, searchText)}
      />
    </React.Fragment>
  );
};

export default Users;
